/**
 * ProcurementIncludedPane: Right sidebar listing included (selected) files
 * in a procurement package. Files are grouped by folder with folder headers.
 * Virtualized file list with confidence badges, feedback actions, and drag-drop.
 */
import { memo, useCallback, useMemo, useRef, useState, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  ChevronRight,
  Folder,
  GripVertical,
  HelpCircle,
  Search,
  Send,
  ThumbsDown,
  ThumbsUp,
  Trash2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getFileIcon } from "@/lib/fileIcons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import type { TitleBlockData } from "@/features/procurement/types";
import { useRubberBand } from "@/hooks/useRubberBand";
import type { ProcurementFileItem } from "./ProcurementCatalogPane";

// ── Props ──

export interface ProcurementIncludedPaneProps {
  items: ProcurementFileItem[];
  highlightedFileIds?: string[];
  // Drag-and-drop
  onDragStart: (
    pane: "catalog" | "included",
    fileId: string,
    e: React.DragEvent<HTMLDivElement>,
  ) => void;
  onDragEnd: () => void;
  onDragOver: (pane: "catalog" | "included", e: React.DragEvent) => void;
  onDragLeave: (pane: "catalog" | "included", e: React.DragEvent) => void;
  onDrop: (pane: "catalog" | "included", e: React.DragEvent) => void;
  isDragOver: boolean;
  // Actions
  onToggleIncluded: (fileId: string, shouldInclude: boolean) => void;
  onFeedback?: (
    fileId: string,
    fb: "keep" | "remove" | "should_include" | "confirm" | "doublecheck",
  ) => void;
  feedbackByFile?: Record<string, "keep" | "remove" | "should_include" | "confirm" | "doublecheck">;
  feedbackCommentsByFile?: Record<string, string>;
  onCommentChange?: (fileId: string, comment: string) => void;
  onCommentSubmit?: (fileId: string) => void;
  isUpdatePending: boolean;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  // Active file (for center preview)
  activePreviewId?: string;
  onActiveChange?: (fileId: string) => void;
  // Unsaved changes indicator
  hasUnsavedChanges: boolean;
  stagedCount: number;
  // Collapse callback
  onCollapse?: () => void;
  // Title-block metadata (for visual coherency with catalog)
  selectedProperties?: string[];
  getTitleBlockProperty?: (tb: TitleBlockData | null | undefined, prop: string) => string | null;
  // Multi-select (rubber band + click)
  selectedIds: Set<string>;
  onSelect: (fileId: string, e: React.MouseEvent) => void;
  onSelectFolder: (ids: string[]) => void;
  onEmptyClick?: () => void;
  // Active pane indicator
  isActivePane?: boolean;
}

// ── Helpers ──

const FILE_ROW_HEIGHT = 52;
const FOLDER_ROW_HEIGHT = 28;

const getFileFolder = (name: string): string => {
  const lastSlash = name.lastIndexOf("/");
  return lastSlash > 0 ? name.substring(0, lastSlash) : "";
};

const getFileBasename = (name: string): string => {
  const lastSlash = name.lastIndexOf("/");
  return lastSlash >= 0 ? name.substring(lastSlash + 1) : name;
};

const formatConfidence = (c?: number): string | undefined => {
  if (c === undefined) return undefined;
  const v = c <= 1 ? c * 100 : c;
  return `${Math.round(v)}%`;
};

const confidenceColorClass = (c?: number): string => {
  if (c === undefined) return "bg-zinc-100 text-zinc-600";
  const v = c <= 1 ? Math.round(c * 100) : Math.round(c);
  if (v >= 70) return "bg-success-muted text-success";
  if (v >= 40) return "bg-amber-100 text-amber-700";
  return "bg-zinc-100 text-zinc-600";
};

// ── Row types for grouped list ──

type ListRow =
  | { type: "folder"; folder: string; count: number }
  | { type: "file"; item: ProcurementFileItem };

function buildGroupedRows(items: ProcurementFileItem[]): ListRow[] {
  const rows: ListRow[] = [];
  let lastFolder: string | null = null;

  for (const item of items) {
    const folder = getFileFolder(item.fileName);
    if (folder !== lastFolder) {
      if (folder) {
        const count = items.filter((i) => getFileFolder(i.fileName) === folder).length;
        rows.push({ type: "folder", folder, count });
      }
      lastFolder = folder;
    }
    rows.push({ type: "file", item });
  }
  return rows;
}

// ── File list item ──

interface FileListItemProps {
  item: ProcurementFileItem;
  isActive: boolean;
  isSelected: boolean;
  isHighlighted: boolean;
  showFolder: boolean;
  activeFeedback?: "keep" | "remove" | "should_include" | "confirm" | "doublecheck";
  feedbackComment?: string;
  onSelect: (fileId: string, e: React.MouseEvent) => void;
  onActiveChange?: (fileId: string) => void;
  onRemove: () => void;
  onFeedback?: (fb: "keep" | "remove" | "doublecheck") => void;
  onCommentChange?: (comment: string) => void;
  onCommentSubmit?: () => void;
  onDragStart: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
  isUpdatePending: boolean;
  selectedProperties?: string[];
  getTitleBlockProperty?: (tb: TitleBlockData | null | undefined, prop: string) => string | null;
}

const FileListItem = memo(function FileListItem({
  item,
  isActive,
  isSelected,
  isHighlighted,
  showFolder,
  activeFeedback,
  feedbackComment,
  onSelect,
  onActiveChange,
  onRemove,
  onFeedback,
  onCommentChange,
  onCommentSubmit,
  onDragStart,
  onDragEnd,
  isUpdatePending,
  selectedProperties,
  getTitleBlockProperty,
}: FileListItemProps) {
  const { t } = useTranslation();
  const [commentPopoverOpen, setCommentPopoverOpen] = useState(false);
  const basename = getFileBasename(item.fileName);
  const confidenceLabel = formatConfidence(item.evaluation?.confidence);
  const colorClass = confidenceColorClass(item.evaluation?.confidence);

  // Title-block property values (matching catalog display)
  const propertyElements =
    selectedProperties && getTitleBlockProperty && item.titleBlockData
      ? selectedProperties
          .map((prop) => {
            const value = getTitleBlockProperty(item.titleBlockData, prop);
            if (!value) return null;
            return (
              <span key={prop}>
                <span className="font-semibold">
                  {t(`procurement.packageDetail.titleBlock.${prop}`)}:
                </span>{" "}
                {value}
              </span>
            );
          })
          .filter(Boolean)
      : [];
  const hasProperties = propertyElements.length > 0;

  const handleFeedbackClick = (fb: "keep" | "remove" | "doublecheck", e: React.MouseEvent) => {
    e.stopPropagation();
    onFeedback?.(fb);
    // Auto-open comment popover if setting new feedback (not toggling off)
    if (activeFeedback !== fb) {
      setCommentPopoverOpen(true);
    } else {
      setCommentPopoverOpen(false);
    }
  };

  return (
    <div
      data-file-id={item.fileId}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("button")) return;
        onSelect(item.fileId, e);
        if (!e.ctrlKey && !e.metaKey && !e.shiftKey) {
          onActiveChange?.(item.fileId);
        }
      }}
      className={cn(
        "relative flex items-start gap-2 pl-2 pr-6 py-1.5 cursor-default select-none transition-colors group",
        "rounded-md border",
        isActive
          ? "border-primary bg-primary/5"
          : isHighlighted
            ? "border-info-border bg-info-muted/45"
            : isSelected
              ? "bg-accent border-border"
              : "border-transparent hover:bg-orange-50 hover:border-orange-200",
      )}
    >
      {/* File icon */}
      <span className="mt-0.5 shrink-0">{getFileIcon(basename)}</span>

      {/* Text content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate leading-tight">{basename}</p>
        {/* Show folder only for ungrouped (root-level) files */}
        {showFolder && (
          <p className="text-[10px] text-muted-foreground truncate leading-tight mt-0.5">
            {getFileFolder(item.fileName)}
          </p>
        )}
        <div className="flex items-center gap-1 mt-0.5">
          {confidenceLabel && (
            <span
              className={cn(
                "text-[10px] inline-flex items-center rounded-full px-1.5 py-1 leading-none",
                colorClass,
              )}
              title={item.evaluation?.reason || undefined}
            >
              {confidenceLabel}
            </span>
          )}
          {item.isManual && (
            <span className="text-[10px] inline-flex items-center rounded-full bg-zinc-100 px-1.5 py-1 leading-none text-zinc-600">
              {t("procurement.packageDetail.manualTag")}
            </span>
          )}
          {/* Inline feedback buttons */}
          {onFeedback && (
            <>
              <Popover
                open={commentPopoverOpen && activeFeedback === "keep"}
                onOpenChange={(open) => {
                  if (!open) setCommentPopoverOpen(false);
                }}
              >
                <PopoverTrigger asChild>
                  <button
                    className={cn(
                      "h-4 w-4 inline-flex items-center justify-center rounded-sm transition-colors",
                      activeFeedback === "keep"
                        ? "text-success"
                        : "text-muted-foreground/40 hover:text-success",
                    )}
                    onClick={(e) => handleFeedbackClick("keep", e)}
                    aria-label={t("procurement.packageDetail.feedback.keep")}
                  >
                    <ThumbsUp className="h-2.5 w-2.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-72 p-3"
                  side="bottom"
                  align="start"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex flex-col gap-2">
                    <p className="text-sm font-medium">
                      {t("procurement.packageDetail.feedback.commentLabel")}
                    </p>
                    <Textarea
                      value={feedbackComment ?? ""}
                      onChange={(e) => onCommentChange?.(e.target.value)}
                      placeholder={t("procurement.packageDetail.feedback.commentPlaceholder")}
                      className="min-h-[80px] text-sm resize-none"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          onCommentSubmit?.();
                          setCommentPopoverOpen(false);
                        }
                      }}
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCommentPopoverOpen(false)}
                      >
                        {t("common.cancel")}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          onCommentSubmit?.();
                          setCommentPopoverOpen(false);
                        }}
                      >
                        <Send className="size-4 mr-1" />
                        {t("common.save")}
                      </Button>
                    </div>
                    {feedbackComment && (
                      <div className="text-xs text-muted-foreground mt-2 pt-2 border-t">
                        <span className="font-medium">
                          {t("procurement.packageDetail.feedback.commentLabel")}:
                        </span>{" "}
                        {feedbackComment}
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              <Popover
                open={commentPopoverOpen && activeFeedback === "remove"}
                onOpenChange={(open) => {
                  if (!open) setCommentPopoverOpen(false);
                }}
              >
                <PopoverTrigger asChild>
                  <button
                    className={cn(
                      "h-4 w-4 inline-flex items-center justify-center rounded-sm transition-colors",
                      activeFeedback === "remove"
                        ? "text-destructive"
                        : "text-muted-foreground/40 hover:text-destructive",
                    )}
                    onClick={(e) => handleFeedbackClick("remove", e)}
                    aria-label={t("procurement.packageDetail.feedback.remove")}
                  >
                    <ThumbsDown className="h-2.5 w-2.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-72 p-3"
                  side="bottom"
                  align="start"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex flex-col gap-2">
                    <p className="text-sm font-medium">
                      {t("procurement.packageDetail.feedback.commentLabel")}
                    </p>
                    <Textarea
                      value={feedbackComment ?? ""}
                      onChange={(e) => onCommentChange?.(e.target.value)}
                      placeholder={t("procurement.packageDetail.feedback.commentPlaceholder")}
                      className="min-h-[80px] text-sm resize-none"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          onCommentSubmit?.();
                          setCommentPopoverOpen(false);
                        }
                      }}
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCommentPopoverOpen(false)}
                      >
                        {t("common.cancel")}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          onCommentSubmit?.();
                          setCommentPopoverOpen(false);
                        }}
                      >
                        <Send className="size-4 mr-1" />
                        {t("common.save")}
                      </Button>
                    </div>
                    {feedbackComment && (
                      <div className="text-xs text-muted-foreground mt-2 pt-2 border-t">
                        <span className="font-medium">
                          {t("procurement.packageDetail.feedback.commentLabel")}:
                        </span>{" "}
                        {feedbackComment}
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              <Popover
                open={commentPopoverOpen && activeFeedback === "doublecheck"}
                onOpenChange={(open) => {
                  if (!open) setCommentPopoverOpen(false);
                }}
              >
                <PopoverTrigger asChild>
                  <button
                    className={cn(
                      "h-4 w-4 inline-flex items-center justify-center rounded-sm transition-colors",
                      activeFeedback === "doublecheck"
                        ? "text-destructive"
                        : "text-muted-foreground/40 hover:text-destructive",
                    )}
                    onClick={(e) => handleFeedbackClick("doublecheck", e)}
                    aria-label={t("procurement.packageDetail.feedback.doublecheck")}
                  >
                    <HelpCircle className="h-2.5 w-2.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-72 p-3"
                  side="bottom"
                  align="start"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex flex-col gap-2">
                    <p className="text-sm font-medium">
                      {t("procurement.packageDetail.feedback.doublecheck")}
                    </p>
                    <Textarea
                      value={feedbackComment ?? ""}
                      onChange={(e) => onCommentChange?.(e.target.value)}
                      placeholder={t(
                        "procurement.packageDetail.feedback.doublecheckCommentPlaceholder",
                      )}
                      className="min-h-[80px] text-sm resize-none"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          onCommentSubmit?.();
                          setCommentPopoverOpen(false);
                        }
                      }}
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCommentPopoverOpen(false)}
                      >
                        {t("common.cancel")}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          onCommentSubmit?.();
                          setCommentPopoverOpen(false);
                        }}
                      >
                        <Send className="size-4 mr-1" />
                        {t("common.save")}
                      </Button>
                    </div>
                    {feedbackComment && (
                      <div className="text-xs text-muted-foreground mt-2 pt-2 border-t">
                        <span className="font-medium">
                          {t("procurement.packageDetail.feedback.commentLabel")}:
                        </span>{" "}
                        {feedbackComment}
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </>
          )}
          {/* Feedback badge for active feedback */}
          {activeFeedback === "keep" && (
            <span className="text-[10px] inline-flex items-center rounded-full bg-success-muted text-success px-1.5 py-1 leading-none">
              {t("procurement.packageDetail.feedback.keep")}
            </span>
          )}
          {activeFeedback === "remove" && (
            <span className="text-[10px] inline-flex items-center rounded-full bg-destructive/15 text-destructive px-1.5 py-1 leading-none">
              {t("procurement.packageDetail.feedback.remove")}
            </span>
          )}
          {activeFeedback === "doublecheck" && (
            <span className="text-[10px] inline-flex items-center rounded-full bg-amber-100 text-amber-700 px-1.5 py-1 leading-none">
              {t("procurement.packageDetail.feedback.doublecheck")}
            </span>
          )}
        </div>
        {/* Title-block properties (matching catalog) */}
        {hasProperties && (
          <div className="text-ui-body-xs line-clamp-2 flex flex-wrap gap-x-3 gap-y-0.5 text-muted-foreground mt-0.5">
            {propertyElements}
          </div>
        )}
        {/* Feedback comment — always visible when present, shown on hover otherwise */}
        {feedbackComment && activeFeedback && (
          <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1 group-hover:line-clamp-3 italic">
            &ldquo;{feedbackComment}&rdquo;
          </p>
        )}
      </div>

      {/* Hover action: remove button only */}
      <div className="absolute top-1/2 -translate-y-1/2 right-0.5 flex items-center gap-px opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          className="h-5 w-5 flex items-center justify-center rounded-sm text-muted-foreground hover:text-destructive hover:bg-accent transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          disabled={isUpdatePending}
          aria-label={t("procurement.packageDetail.actions.removeFromPackage")}
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
});

// ── Empty state ──

const EmptyState = () => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center h-full text-center p-8 gap-3">
      <div className="rounded-full bg-muted/50 p-4">
        <GripVertical className="h-8 w-8 text-muted-foreground/40" />
      </div>
      <p className="text-sm text-muted-foreground">
        {t("procurement.packageDetail.included.empty")}
      </p>
      <p className="text-xs text-muted-foreground/60 max-w-48">
        {t("procurement.packageDetail.included.emptyHint")}
      </p>
    </div>
  );
};

// ── Main component ──

export const ProcurementIncludedPane = ({
  items,
  highlightedFileIds = [],
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  isDragOver,
  onToggleIncluded,
  onFeedback,
  feedbackByFile,
  feedbackCommentsByFile,
  onCommentChange,
  onCommentSubmit,
  isUpdatePending,
  searchQuery,
  onSearchChange,
  activePreviewId,
  onActiveChange,
  hasUnsavedChanges,
  stagedCount,
  onCollapse,
  selectedIds,
  onSelect,
  onSelectFolder,
  onEmptyClick,
  selectedProperties,
  getTitleBlockProperty,
  isActivePane,
}: ProcurementIncludedPaneProps) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);

  const hasProps = Boolean(selectedProperties?.length);

  // Filter items by search
  const filteredItems = useMemo(() => {
    if (!searchQuery) return items;
    const q = searchQuery.trim().toLowerCase();
    return items.filter((item) => item.fileName.toLowerCase().includes(q));
  }, [items, searchQuery]);

  // Build grouped rows (folder headers + files)
  const groupedRows = useMemo(() => buildGroupedRows(filteredItems), [filteredItems]);

  // Virtualizer — row height adapts when properties are shown
  const fileRowHeight = hasProps ? FILE_ROW_HEIGHT + 16 : FILE_ROW_HEIGHT;
  const virtualizer = useVirtualizer({
    count: groupedRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (i) => (groupedRows[i].type === "folder" ? FOLDER_ROW_HEIGHT : fileRowHeight),
    overscan: 8,
  });

  // Rubber-band (lasso) multi-select
  const getItemLayout = useCallback(() => {
    const items: Array<{ id: string; top: number; height: number }> = [];
    let top = 0;
    for (const row of groupedRows) {
      const height = row.type === "folder" ? FOLDER_ROW_HEIGHT : fileRowHeight;
      if (row.type === "file") {
        items.push({ id: row.item.fileId, top, height });
      }
      top += height;
    }
    return items;
  }, [groupedRows, fileRowHeight]);

  const rubberBand = useRubberBand({
    containerRef: scrollRef as RefObject<HTMLElement>,
    itemSelector: "[data-file-id]",
    onSelectionChange: onSelectFolder,
    isEnabled: true,
    baseSelection: Array.from(selectedIds),
    getItemLayout,
  });

  // Container click handler (empty-space click clears selection)
  const handleContainerClick = useCallback(
    (e: React.MouseEvent) => {
      if (rubberBand.justFinishedRef.current) return;
      const target = e.target as HTMLElement;
      if (target.closest("[data-file-id]") || target.closest("button, a, input")) return;
      onEmptyClick?.();
    },
    [rubberBand.justFinishedRef, onEmptyClick],
  );

  if (items.length === 0) {
    return (
      <div
        className={cn(
          "flex flex-col h-full",
          isDragOver && "outline-2 outline-dashed outline-blue-500 -outline-offset-2 bg-blue-500/6",
        )}
        onDragOver={(e) => onDragOver("included", e)}
        onDragLeave={(e) => onDragLeave("included", e)}
        onDrop={(e) => onDrop("included", e)}
      >
        <EmptyState />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col h-full min-h-0",
        isDragOver && "outline-2 outline-dashed outline-blue-500 -outline-offset-2 bg-blue-500/6",
      )}
      onDragOver={(e) => onDragOver("included", e)}
      onDragLeave={(e) => onDragLeave("included", e)}
      onDrop={(e) => onDrop("included", e)}
    >
      {/* Toolbar — matches catalog style */}
      <div
        className={cn(
          "shrink-0 border-b border-border bg-muted/50",
          isActivePane && "border-t-2 border-t-primary",
        )}
      >
        <div
          className={cn(
            "flex h-8 items-center gap-2 px-2",
            onCollapse && "cursor-pointer hover:bg-muted/80 transition-colors",
          )}
          onClick={onCollapse}
        >
          <span className="text-xs font-semibold">
            {t("procurement.packageDetail.included.title")}
          </span>
          <Badge variant="secondary" size="sm">
            {filteredItems.length}
          </Badge>
          {hasUnsavedChanges && (
            <Badge variant="warning" size="sm" className="whitespace-nowrap">
              {t("procurement.packageDetail.unsavedChanges")}
              {stagedCount > 0 ? ` (${stagedCount})` : ""}
            </Badge>
          )}
          <div className="flex-1" />
          {onCollapse && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
        </div>
        <div className="flex items-center gap-1.5 border-t border-border bg-background px-2 py-1">
          <Search className="h-3 w-3 shrink-0 text-muted-foreground/40" />
          <input
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("procurement.packageDetail.included.searchPlaceholder")}
            aria-label={t("procurement.packageDetail.included.searchPlaceholder")}
            className="w-full border-none bg-transparent text-xs outline-none placeholder:text-muted-foreground/50"
          />
        </div>
      </div>

      {/* File list grouped by folder */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto min-h-0 px-3 py-1"
        onClick={handleContainerClick}
        onPointerDown={rubberBand.handlePointerDown}
      >
        <div
          style={{
            height: virtualizer.getTotalSize(),
            position: "relative",
            overflow: "hidden",
          }}
        >
          {virtualizer.getVirtualItems().map((vItem) => {
            const row = groupedRows[vItem.index];
            return (
              <div
                key={vItem.key}
                ref={virtualizer.measureElement}
                data-index={vItem.index}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${vItem.start}px)`,
                }}
              >
                {row.type === "folder" ? (
                  <div className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                    <Folder className="h-3 w-3 text-muted-foreground/50" />
                    <span className="truncate">{row.folder.split("/").pop()}</span>
                    <span className="text-muted-foreground/40 tabular-nums">{row.count}</span>
                  </div>
                ) : (
                  <FileListItem
                    item={row.item}
                    isActive={row.item.fileId === activePreviewId}
                    isSelected={selectedIds.has(row.item.fileId)}
                    isHighlighted={highlightedFileIds.includes(row.item.fileId)}
                    showFolder={!getFileFolder(row.item.fileName)}
                    activeFeedback={feedbackByFile?.[row.item.fileId]}
                    feedbackComment={feedbackCommentsByFile?.[row.item.fileId]}
                    onSelect={onSelect}
                    onActiveChange={onActiveChange}
                    onRemove={() => onToggleIncluded(row.item.fileId, false)}
                    onFeedback={onFeedback ? (fb) => onFeedback(row.item.fileId, fb) : undefined}
                    onCommentChange={
                      onCommentChange
                        ? (comment) => onCommentChange(row.item.fileId, comment)
                        : undefined
                    }
                    onCommentSubmit={
                      onCommentSubmit ? () => onCommentSubmit(row.item.fileId) : undefined
                    }
                    onDragStart={(e) => onDragStart("included", row.item.fileId, e)}
                    onDragEnd={onDragEnd}
                    isUpdatePending={isUpdatePending}
                    selectedProperties={selectedProperties}
                    getTitleBlockProperty={getTitleBlockProperty}
                  />
                )}
              </div>
            );
          })}
          <div
            ref={rubberBand.rubberBandRef}
            className="border border-blue-500/60 bg-blue-500/10 rounded-sm pointer-events-none"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              zIndex: 50,
              display: "none",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ProcurementIncludedPane;
